<template>
  <div class="main container-fluid streams-page">
    <header class="streams-hero">
      <div>
        <h4 class="streams-hero__title mb-1">Стримы</h4>
        <p class="streams-hero__subtitle mb-0">Управляйте каналами публикаций и их настройками</p>
      </div>
      <router-link to="/streams/new" class="btn btn-light btn-sm streams-hero__create">
        <i class="bi bi-plus-lg me-1"></i>
        Создать
      </router-link>
    </header>

    <section class="px-3 pb-2">
      <div class="streams-filter-card">
        <div class="streams-filter-card__title">Фильтры</div>
        <div class="row g-2 align-items-stretch">
          <div class="col">
            <input
              v-model.trim="q"
              type="search"
              class="form-control form-control-sm"
              placeholder="Поиск по названию и описанию..."
            />
          </div>
          <div class="col-auto streams-filter-card__type">
            <select v-model="typeFilter" class="form-select form-select-sm">
              <option value="">Все типы</option>
              <option value="TELEGRAM">TELEGRAM</option>
              <option value="WORDPRESS">WORDPRESS</option>
              <option value="YANDEX_DZEN">YANDEX_DZEN</option>
            </select>
          </div>
        </div>
      </div>
    </section>

    <section v-if="filteredSorted.length" class="px-3 pb-2 streams-meta">
      <small class="streams-meta__count">Найдено: {{ filteredSorted.length }}</small>
      <nav v-if="totalPages > 1">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 }" @click="setPage(currentPage - 1)">
            <a class="page-link">&laquo;</a>
          </li>
          <li
            class="page-item"
            v-for="page in totalPages"
            :key="page"
            :class="{ active: page === currentPage }"
            @click="setPage(page)"
          >
            <a class="page-link">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }" @click="setPage(currentPage + 1)">
            <a class="page-link">&raquo;</a>
          </li>
        </ul>
      </nav>
    </section>

    <div v-if="loading" class="px-3 pb-2">
      <div class="skeleton-row" v-for="i in 5" :key="i"></div>
    </div>

    <div v-else-if="filteredSorted.length === 0" class="streams-empty px-3 pb-3">
      <div class="streams-empty__card">
        <img src="@/assets/illustrations/empty.svg" alt="" class="streams-empty__illu" />
        <div class="streams-empty__title mt-2">Пока нет стримов</div>
        <small class="text-muted">Нажмите «Создать», чтобы добавить первый</small>
        <div class="mt-3">
          <router-link to="/streams/new" class="btn btn-primary btn-sm">
            <i class="bi bi-plus-lg me-1"></i>
            Создать стрим
          </router-link>
        </div>
      </div>
    </div>

    <div v-else class="streams-list px-3 pb-4">
      <article
        class="stream-row"
        :class="{
          'stream-row--dragging': isDragging(stream.id),
          'stream-row--drag-over-before': isDragOverBefore(stream.id),
          'stream-row--drag-over-after': isDragOverAfter(stream.id),
        }"
        v-for="stream in pagedStreams"
        :key="stream.id"
        draggable="true"
        @dragstart="onDragStart(stream.id, $event)"
        @dragover="onDragOver(stream.id, $event)"
        @drop="onDrop(stream.id, $event)"
        @dragend="onDragEnd"
      >
        <div class="stream-main">
          <div class="stream-main-top">
            <img :src="getStreamIcon(stream.type)" class="stream-icon" alt="" />
            <h6 class="stream-name mb-0">{{ stream.name || `Stream #${stream.id}` }}</h6>
            <span class="stream-pill">{{ stream.type || 'UNKNOWN' }}</span>
            <span v-if="stream.settings?.aiProvider" class="stream-pill stream-pill--secondary">
              {{ stream.settings.aiProvider }}
            </span>
          </div>
          <div class="stream-desc">{{ stream.description || '—' }}</div>
          <div class="stream-stats">
            <span class="stream-stat" title="Создано материалов">
              <i class="bi bi-file-earmark-text"></i>
              <span class="stream-stat__num">{{ stream.contentsTotal ?? 0 }}</span>
              <span class="stream-stat__label">создано</span>
            </span>
            <span class="stream-stat stream-stat--published" title="Опубликовано материалов">
              <i class="bi bi-check2-circle"></i>
              <span class="stream-stat__num">{{ stream.publishedCount ?? 0 }}</span>
              <span class="stream-stat__label">опубликовано</span>
            </span>
          </div>
        </div>

        <div class="stream-actions">
          <!-- cmaker controls in a fixed-width left slot so the buttons below
               line up across rows whether or not a stream has cmaker. -->
          <div class="stream-actions__cmaker">
            <div v-if="stream.type === 'WORDPRESS'" class="cmaker-schedule">
              <div
                v-if="schedInterval(stream.id)"
                class="schedule-interval"
                :title="`Запуск каждые ${formatHours(schedInterval(stream.id))} ч`"
              >
                <i class="bi bi-arrow-repeat"></i>
                <span class="schedule-interval__num">{{ formatHours(schedInterval(stream.id)) }}</span>
                <span class="schedule-interval__unit">ч</span>
              </div>
              <div class="action-group">
                <span class="action-group__label">
                  <i class="bi bi-calendar-week"></i>
                  Расписание
                </span>
              <div class="schedule-controls" role="group">
                <button
                  type="button"
                  class="btn btn-sm schedule-btn"
                  :class="playBtnClass(stream.id)"
                  :disabled="!canPlay(stream.id) || schedBusy(stream.id)"
                  :title="playTitle(stream.id)"
                  @click="onPlay(stream)"
                >
                  <span v-if="schedBusy(stream.id)" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="bi bi-play-fill"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm schedule-btn"
                  :class="stopBtnClass(stream.id)"
                  :disabled="!canStop(stream.id) || schedBusy(stream.id)"
                  title="Выключить расписание"
                  @click="onStop(stream)"
                >
                  <i class="bi bi-stop-fill"></i>
                </button>
                <router-link
                  :to="`/streams/${stream.id}/schedule`"
                  class="btn btn-outline-secondary btn-sm schedule-btn"
                  title="Настройки расписания"
                >
                  <i class="bi bi-gear"></i>
                </router-link>
              </div>
              </div>
            </div>
          </div>
          <div class="action-group">
            <span class="action-group__label">
              <i class="bi bi-sliders2"></i>
              Управление
            </span>
            <div class="action-group__row">
              <router-link
                :to="`/streams/${stream.id}/edit`"
                class="btn btn-outline-secondary btn-sm btn-icon"
                title="Настройки стрима"
              >
                <i class="bi bi-gear"></i>
              </router-link>
              <router-link
                :to="`/streams/${stream.id}/content`"
                class="btn btn-primary btn-sm"
                title="Контент"
              >
                <i class="bi bi-file-text me-1"></i>
                Контент
              </router-link>
              <button
                type="button"
                class="btn btn-outline-danger btn-sm btn-icon"
                title="Удалить стрим"
                @click="openDeleteModal(stream)"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="move-controls">
          <button
            type="button"
            class="btn btn-light border btn-sm"
            title="Переместить вверх"
            :disabled="isMoveUpDisabled(stream.id)"
            @click="moveStream(stream.id, -1)"
          >
            <i class="bi bi-chevron-up"></i>
          </button>
          <button
            type="button"
            class="btn btn-light border btn-sm"
            title="Переместить вниз"
            :disabled="isMoveDownDisabled(stream.id)"
            @click="moveStream(stream.id, 1)"
          >
            <i class="bi bi-chevron-down"></i>
          </button>
        </div>
      </article>
    </div>

    <div v-if="showDeleteModal" class="modal-backdrop delete-modal-backdrop" @click.self="closeDeleteModal">
      <div class="modal-container delete-modal-container">
        <div class="delete-modal-head">
          <div class="delete-modal-icon"><i class="bi bi-shield-lock"></i></div>
          <div class="delete-modal-title-wrap">
            <h6 class="delete-modal-title mb-0">Подтвердите удаление</h6>
            <div class="delete-modal-subtitle">Введите текущий пароль аккаунта</div>
          </div>
          <button type="button" class="delete-modal-close" :disabled="deleteLoading" @click="closeDeleteModal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="delete-modal-body">
          <div class="delete-modal-stream">
            <span class="text-muted">Стрим:</span>
            <strong>{{ deleteTarget?.name || `#${deleteTarget?.id || ''}` }}</strong>
          </div>
          <label class="form-label mb-1 mt-2">Текущий пароль</label>
          <div class="input-group input-group-sm">
            <span class="input-group-text"><i class="bi bi-key"></i></span>
            <input
              v-model="deletePassword"
              type="password"
              class="form-control delete-modal-input"
              :disabled="deleteLoading"
              autocomplete="current-password"
              @keyup.enter="confirmDeleteStream"
            />
          </div>

          <div v-if="deleteError" class="alert alert-danger py-1 px-2 small mt-2 mb-0">{{ deleteError }}</div>
        </div>

        <div class="delete-modal-footer">
          <button class="btn btn-secondary btn-sm me-2" :disabled="deleteLoading" @click="closeDeleteModal">Отмена</button>
          <button class="btn btn-danger btn-sm" :disabled="deleteLoading" @click="confirmDeleteStream">
            <span v-if="deleteLoading" class="spinner-border spinner-border-sm me-1"></span>
            Проверить и удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { getSchedule, setScheduleEnabled } from '@/services/streamScheduleService'

const STREAM_ORDER_STORAGE_KEY = 'streams:custom-order:v1'

const normalizeId = value => String(value)
const STREAM_ROW_APPROX_HEIGHT = 66
const STREAMS_SCREEN_RESERVED_HEIGHT = 230

const parseDateMs = value => {
  if (!value) return 0
  const ms = new Date(value).getTime()
  return Number.isFinite(ms) ? ms : 0
}

// Human label for an hour interval: integers stay as-is, fractions use a Russian
// decimal comma and at most 2 decimals (0.5 → "0,5", 0.333… → "0,33").
const formatHours = n => {
  if (n == null) return ''
  const num = Number(n)
  if (!Number.isFinite(num)) return ''
  return String(Math.round(num * 100) / 100).replace('.', ',')
}

const defaultSortByCreatedDesc = list =>
  list.slice().sort((a, b) => {
    const diff = parseDateMs(b.createdAt) - parseDateMs(a.createdAt)
    if (diff !== 0) return diff
    return Number(b.id || 0) - Number(a.id || 0)
  })

const loadSavedOrder = () => {
  try {
    const raw = localStorage.getItem(STREAM_ORDER_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.map(normalizeId) : []
  } catch {
    return []
  }
}

const saveOrder = ids => {
  try {
    localStorage.setItem(STREAM_ORDER_STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // ignore storage errors
  }
}

const calculatePageSize = () => {
  if (typeof window === 'undefined') return 10
  const viewportHeight = Number(window.innerHeight || 0)
  if (!viewportHeight) return 10
  const availableHeight = Math.max(0, viewportHeight - STREAMS_SCREEN_RESERVED_HEIGHT)
  return Math.max(1, Math.floor(availableHeight / STREAM_ROW_APPROX_HEIGHT) - 1)
}

export default {
  name: 'StreamsPage',
  setup() {
    const streams = ref([])
    const loading = ref(true)

    const q = ref('')
    const typeFilter = ref('')
    const savedOrderIds = ref(loadSavedOrder())
    const currentPage = ref(1)
    const pageSize = ref(calculatePageSize())
    const dragSourceId = ref('')
    const dragOverId = ref('')
    const dragOverPosition = ref('before')
    const showDeleteModal = ref(false)
    const deleteTarget = ref(null)
    const deletePassword = ref('')
    const deleteError = ref('')
    const deleteLoading = ref(false)

    // Per-stream schedule state for the play/stop/settings controls.
    // Keyed by stream id: { exists, enabled, canEnable, busy }.
    const scheduleStates = reactive({})

    const emptySchedState = () => ({ exists: false, enabled: false, canEnable: false, busy: false, intervalHours: null })
    const schedState = id => scheduleStates[id] || emptySchedState()
    const schedBusy = id => schedState(id).busy === true
    const schedInterval = id => schedState(id).intervalHours
    const canPlay = id => {
      const s = schedState(id)
      return s.exists && s.canEnable && !s.enabled
    }
    const canStop = id => {
      const s = schedState(id)
      return s.exists && s.enabled
    }
    const playBtnClass = id => {
      const s = schedState(id)
      if (s.exists && s.enabled) return 'btn-success'
      if (canPlay(id)) return 'btn-outline-success'
      return 'btn-outline-secondary'
    }
    const stopBtnClass = id => (canStop(id) ? 'btn-outline-danger' : 'btn-outline-secondary')
    const playTitle = id => {
      const s = schedState(id)
      if (!s.exists) return 'Расписание не настроено'
      if (s.enabled) return 'Расписание включено'
      if (!s.canEnable) return 'Нельзя включить: проверьте SiteProject и активные RSS-каналы'
      return 'Включить расписание'
    }

    const applyScheduleDto = (id, dto) => {
      scheduleStates[id] = dto
        ? { exists: true, enabled: !!dto.enabled, canEnable: !!dto.canEnable, busy: false, intervalHours: dto.intervalHours }
        : emptySchedState()
    }

    const refreshScheduleState = async id => {
      try {
        applyScheduleDto(id, await getSchedule(id))
      } catch (e) {
        console.error(e)
        scheduleStates[id] = emptySchedState()
      }
    }

    const loadScheduleStates = async () => {
      const wp = streams.value.filter(s => s.type === 'WORDPRESS')
      wp.forEach(s => { scheduleStates[s.id] = emptySchedState() })
      await Promise.all(wp.map(s => refreshScheduleState(s.id)))
    }

    const setEnabled = async (stream, enabled) => {
      const id = stream.id
      const current = scheduleStates[id] || emptySchedState()
      if (current.busy) return
      scheduleStates[id] = { ...current, busy: true }
      try {
        applyScheduleDto(id, await setScheduleEnabled(id, enabled))
      } catch (e) {
        console.error(e)
        await refreshScheduleState(id)
      }
    }

    const onPlay = stream => setEnabled(stream, true)
    const onStop = stream => setEnabled(stream, false)

    const syncSavedOrderWithStreams = () => {
      const defaultIds = defaultSortByCreatedDesc(streams.value).map(x => normalizeId(x.id))
      const available = new Set(defaultIds)
      const existing = savedOrderIds.value.filter(id => available.has(id))
      const existingSet = new Set(existing)
      const missing = defaultIds.filter(id => !existingSet.has(id))
      savedOrderIds.value = [...missing, ...existing]
      saveOrder(savedOrderIds.value)
    }

    const fetchStreams = async () => {
      loading.value = true
      try {
        const collected = []
        const size = 100
        let page = 0
        let keepLoading = true

        while (keepLoading) {
          const { data } = await axios.get('/api/streams', {
            params: {
              page,
              size,
              sortBy: 'createdAt',
              sortDir: 'desc',
            },
          })

          if (Array.isArray(data)) {
            collected.push(...data)
            break
          }

          const chunk = Array.isArray(data?.content) ? data.content : []
          collected.push(...chunk)

          // Spring Boot VIA_DTO nests paging metadata under `page`; fall back to
          // legacy top-level fields. Without this the loop stops after page 0.
          const pageMeta = data?.page ?? data
          const totalPages = Number(pageMeta?.totalPages || 0)
          const isLast = data?.last === true || totalPages === 0 || page >= totalPages - 1
          keepLoading = !isLast
          page += 1

          if (page > 500) keepLoading = false
        }

        streams.value = collected
        syncSavedOrderWithStreams()
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const orderedStreams = computed(() => {
      const map = new Map(streams.value.map(s => [normalizeId(s.id), s]))
      const result = []
      const used = new Set()

      savedOrderIds.value.forEach(id => {
        const stream = map.get(id)
        if (stream) {
          result.push(stream)
          used.add(id)
        }
      })

      if (result.length < streams.value.length) {
        defaultSortByCreatedDesc(streams.value).forEach(stream => {
          const id = normalizeId(stream.id)
          if (!used.has(id)) result.push(stream)
        })
      }

      return result
    })

    const filteredSorted = computed(() => {
      let arr = orderedStreams.value.slice()

      if (q.value) {
        const query = q.value.toLowerCase()
        arr = arr.filter(x =>
          (x.name || '').toLowerCase().includes(query) ||
          (x.description || '').toLowerCase().includes(query)
        )
      }

      if (typeFilter.value) {
        arr = arr.filter(x => x.type === typeFilter.value)
      }

      return arr
    })

    const totalPages = computed(() => Math.max(1, Math.ceil(filteredSorted.value.length / pageSize.value)))

    const pagedStreams = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      return filteredSorted.value.slice(start, end)
    })

    const setPage = page => {
      const next = Math.min(totalPages.value, Math.max(1, Number(page || 1)))
      currentPage.value = next
    }

    const updatePageSize = () => {
      pageSize.value = calculatePageSize()
    }

    watch([q, typeFilter], () => {
      currentPage.value = 1
    })

    watch(totalPages, value => {
      if (currentPage.value > value) currentPage.value = value
    })

    const getFilteredIndex = streamId =>
      filteredSorted.value.findIndex(x => normalizeId(x.id) === normalizeId(streamId))

    const isMoveUpDisabled = streamId => getFilteredIndex(streamId) <= 0
    const isMoveDownDisabled = streamId => {
      const idx = getFilteredIndex(streamId)
      return idx < 0 || idx >= filteredSorted.value.length - 1
    }

    const moveStream = (streamId, direction) => {
      const filteredIds = filteredSorted.value.map(x => normalizeId(x.id))
      const currentId = normalizeId(streamId)
      const currentIndex = filteredIds.indexOf(currentId)
      const nextIndex = currentIndex + direction

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= filteredIds.length) return

      const targetId = filteredIds[nextIndex]
      const nextOrder = savedOrderIds.value.slice()
      const posA = nextOrder.indexOf(currentId)
      const posB = nextOrder.indexOf(targetId)

      if (posA < 0 || posB < 0) return

      ;[nextOrder[posA], nextOrder[posB]] = [nextOrder[posB], nextOrder[posA]]
      savedOrderIds.value = nextOrder
      saveOrder(savedOrderIds.value)
    }

    const clearDragState = () => {
      dragSourceId.value = ''
      dragOverId.value = ''
      dragOverPosition.value = 'before'
    }

    const reorderByDrop = (sourceIdRaw, targetIdRaw, position = 'before') => {
      const sourceId = normalizeId(sourceIdRaw)
      const targetId = normalizeId(targetIdRaw)
      if (!sourceId || !targetId || sourceId === targetId) return

      const nextOrder = savedOrderIds.value.slice()
      const sourceIndex = nextOrder.indexOf(sourceId)
      const targetIndex = nextOrder.indexOf(targetId)
      if (sourceIndex < 0 || targetIndex < 0) return

      nextOrder.splice(sourceIndex, 1)
      const targetPosAfterRemove = nextOrder.indexOf(targetId)
      const insertIndex = position === 'after' ? targetPosAfterRemove + 1 : targetPosAfterRemove
      nextOrder.splice(insertIndex, 0, sourceId)

      savedOrderIds.value = nextOrder
      saveOrder(savedOrderIds.value)
    }

    const onDragStart = (streamId, event) => {
      dragSourceId.value = normalizeId(streamId)
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', dragSourceId.value)
      }
    }

    const onDragOver = (streamId, event) => {
      const targetId = normalizeId(streamId)
      if (!dragSourceId.value || dragSourceId.value === targetId) return
      event.preventDefault()

      if (event?.dataTransfer) event.dataTransfer.dropEffect = 'move'

      dragOverId.value = targetId
      const rect = event.currentTarget?.getBoundingClientRect?.()
      if (!rect) {
        dragOverPosition.value = 'before'
        return
      }
      dragOverPosition.value = event.clientY > rect.top + rect.height / 2 ? 'after' : 'before'
    }

    const onDrop = (streamId, event) => {
      event.preventDefault()
      const targetId = normalizeId(streamId)
      const sourceId = dragSourceId.value || String(event?.dataTransfer?.getData('text/plain') || '')
      if (!sourceId || sourceId === targetId) {
        clearDragState()
        return
      }
      const position = dragOverId.value === targetId ? dragOverPosition.value : 'before'
      reorderByDrop(sourceId, targetId, position)
      clearDragState()
    }

    const onDragEnd = () => {
      clearDragState()
    }

    const isDragging = streamId => normalizeId(streamId) === dragSourceId.value
    const isDragOverBefore = streamId =>
      normalizeId(streamId) === dragOverId.value && dragOverPosition.value === 'before'
    const isDragOverAfter = streamId =>
      normalizeId(streamId) === dragOverId.value && dragOverPosition.value === 'after'

    const formatDate = value => {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString()
    }

    const getStreamIcon = type => {
      const icons = {
        TELEGRAM: require('@/assets/icons/telegram.png'),
        X: require('@/assets/icons/x.png'),
        YANDEX_DZEN: require('@/assets/icons/zen.png'),
        WORDPRESS: require('@/assets/icons/wordpress.png'),
      }
      return icons[type] || require('@/assets/icons/default.png')
    }

    const openDeleteModal = stream => {
      deleteTarget.value = stream
      deletePassword.value = ''
      deleteError.value = ''
      showDeleteModal.value = true
    }

    const closeDeleteModal = () => {
      showDeleteModal.value = false
      deleteTarget.value = null
      deletePassword.value = ''
      deleteError.value = ''
    }

    const confirmDeleteStream = async () => {
      if (!deleteTarget.value) return
      if (!String(deletePassword.value || '').trim()) {
        deleteError.value = 'Введите пароль.'
        return
      }

      deleteLoading.value = true
      deleteError.value = ''
      try {
        const verifyResponse = await axios.post(
          '/api/account/verify-password',
          { password: deletePassword.value }
        )
        const isValid = verifyResponse?.data?.data?.valid === true
        if (!isValid) {
          deleteError.value = 'Неверный пароль.'
          return
        }

        await axios.delete(`/api/streams/${deleteTarget.value.id}`)
        await fetchStreams()
        closeDeleteModal()
      } catch (e) {
        console.error('РћС€РёР±РєР° РїСЂРё СѓРґР°Р»РµРЅРёРё СЃС‚СЂРёРјР°:', e)
        deleteError.value = 'Не удалось удалить стрим.'
      } finally {
        deleteLoading.value = false
      }
    }

    onMounted(async () => {
      window.addEventListener('resize', updatePageSize)
      updatePageSize()
      await fetchStreams()
      loadScheduleStates()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updatePageSize)
    })

    return {
      streams,
      loading,
      q,
      typeFilter,
      currentPage,
      totalPages,
      filteredSorted,
      pagedStreams,
      formatDate,
      getStreamIcon,
      setPage,
      openDeleteModal,
      closeDeleteModal,
      confirmDeleteStream,
      showDeleteModal,
      deleteTarget,
      deletePassword,
      deleteError,
      deleteLoading,
      moveStream,
      isMoveUpDisabled,
      isMoveDownDisabled,
      onDragStart,
      onDragOver,
      onDrop,
      onDragEnd,
      isDragging,
      isDragOverBefore,
      isDragOverAfter,
      schedBusy,
      schedInterval,
      formatHours,
      canPlay,
      canStop,
      playBtnClass,
      stopBtnClass,
      playTitle,
      onPlay,
      onStop,
    }
  },
}
</script>

<style scoped>
.streams-page {
  min-height: 100%;
  padding-top: 10px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
}

.streams-hero {
  margin: 0 20px 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 40px rgba(16, 48, 95, 0.22);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.streams-hero__title {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.streams-hero__subtitle {
  color: rgba(232, 242, 255, 0.92);
  font-size: 13px;
}

.streams-hero__create {
  color: #103f77;
  border-color: #cfe2ff;
  font-weight: 700;
  white-space: nowrap;
}

.streams-filter-card {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.streams-filter-card__title {
  display: inline-flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 5px 9px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.streams-filter-card__type {
  width: 250px;
}

.streams-filter-card .form-control,
.streams-filter-card .form-select {
  min-height: 34px;
  font-size: 13px;
  border-color: #d9e4f7;
}

.streams-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.streams-meta__count {
  color: #4f6789;
  font-size: 12px;
  font-weight: 700;
}

.pagination .page-item:not(.disabled) {
  cursor: pointer;
}

.pagination .page-item.disabled {
  cursor: default;
}

.skeleton-row {
  height: 74px;
  background: linear-gradient(90deg, #f2f4f8 25%, #eef1f6 37%, #f2f4f8 63%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 10px;
  margin-bottom: 10px;
}

@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.streams-empty__card {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  text-align: center;
  color: #6c757d;
  padding: 34px 20px;
}

.streams-empty__illu {
  width: 140px;
  opacity: .7;
}

.streams-empty__title {
  color: #243f67;
  font-weight: 700;
  font-size: 16px;
}

.streams-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stream-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #dce7fa;
  border-radius: 12px;
  padding: 10px 12px;
  cursor: grab;
  user-select: none;
  box-shadow: 0 6px 14px rgba(24, 55, 100, 0.05);
  transition: border-color .15s ease, box-shadow .15s ease;
}

.stream-row:hover {
  border-color: #bed5f7;
  box-shadow: 0 8px 18px rgba(24, 55, 100, 0.09);
}

.stream-row:active {
  cursor: grabbing;
}

.stream-row--dragging {
  opacity: .6;
}

.stream-row--drag-over-before {
  box-shadow: inset 0 2px 0 #0d6efd;
}

.stream-row--drag-over-after {
  box-shadow: inset 0 -2px 0 #0d6efd;
}

.move-controls {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-self: center;
  justify-self: end;
}

.move-controls .btn {
  width: 24px;
  height: 24px;
  min-width: 24px;
  padding: 0;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.move-controls .btn i {
  font-size: 11px;
}

.stream-main {
  min-width: 0;
}

.stream-main-top {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.stream-icon {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
}

.stream-name {
  font-size: 15px;
  font-weight: 700;
  color: #1f2d45;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 420px;
}

.stream-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d7e2f6;
  background: #f7faff;
  color: #3d5478;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 8px;
}

.stream-pill--secondary {
  background: #f7f8fb;
  border-color: #dee3ef;
  color: #4f607f;
}

.stream-desc {
  margin-top: 3px;
  color: #6c7a92;
  font-size: 12px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stream-stats {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.stream-stat {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  border: 1px solid #e1e9f7;
  background: #f7faff;
  color: #4f607f;
  font-size: 11px;
  line-height: 1;
  padding: 4px 9px;
}

.stream-stat i {
  font-size: 12px;
  color: #7c8db0;
}

.stream-stat__num {
  font-weight: 800;
  color: #243f67;
}

.stream-stat__label {
  color: #7a89a4;
}

.stream-stat--published {
  border-color: #cdeacd;
  background: #f1faf1;
  color: #2f7d46;
}

.stream-stat--published i,
.stream-stat--published .stream-stat__num {
  color: #2f7d46;
}

.stream-stat--published .stream-stat__label {
  color: #5aa06e;
}

.stream-actions {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: nowrap;
}

.stream-actions .btn,
.stream-actions a.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  font-weight: 600;
  white-space: nowrap;
}

.stream-actions__cmaker {
  /* Fixed width so the "Управление" buttons line up across rows whether a stream
     has a schedule interval shown or not. Sized to fit interval + schedule controls. */
  flex: 0 0 184px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

/* Interval badge sits to the LEFT of the schedule block, bottom-aligned with the
   control buttons (the "Расписание" label floats above them). */
.cmaker-schedule {
  display: inline-flex;
  align-items: flex-end;
  gap: 8px;
}

.schedule-interval {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 34px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid #e1e9f7;
  background: #f7faff;
  color: #3d5478;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  box-sizing: border-box;
}

.schedule-interval i {
  font-size: 12px;
  color: #7c8db0;
}

.schedule-interval__num {
  font-weight: 800;
  color: #243f67;
}

.schedule-interval__unit {
  color: #7a89a4;
}

.stream-actions .btn-icon,
.stream-actions a.btn-icon {
  width: 34px;
  min-width: 34px;
  padding: 0;
  font-size: 15px;
}

/* Both action zones (schedule + management) share this layout so their
   button rows line up on the same baseline under equal-height labels. */
.action-group {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.action-group__label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 12px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6f80a1;
  padding-left: 2px;
}

.action-group__label i {
  font-size: 10px;
}

.action-group__row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
}

.schedule-controls {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 34px;
  padding: 0 5px;
  border-radius: 8px;
  border: 1px solid #e1e9f7;
  background: #f7faff;
  box-sizing: border-box;
}

.schedule-controls .schedule-btn {
  width: 26px;
  height: 26px;
  min-width: 26px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.schedule-controls .schedule-btn i {
  font-size: 14px;
}

.delete-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}

.delete-modal-container {
  background: #fff;
  border-radius: 14px;
  width: 92vw;
  max-width: 460px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.28);
  overflow: hidden;
  border: 1px solid #e5eaf6;
}

.delete-modal-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px 10px;
  border-bottom: 1px solid #edf1f8;
}

.delete-modal-icon {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: #fff3f3;
  color: #dc3545;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex: 0 0 34px;
}

.delete-modal-title-wrap {
  min-width: 0;
}

.delete-modal-title {
  color: #1f2d45;
  font-weight: 700;
}

.delete-modal-subtitle {
  color: #6b7a93;
  font-size: 12px;
  margin-top: 2px;
}

.delete-modal-close {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #7a879c;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.delete-modal-close:hover {
  background: #f3f6fb;
  color: #2f3c54;
}

.delete-modal-body {
  padding: 12px 14px;
}

.delete-modal-stream {
  border: 1px dashed #d8e1f2;
  border-radius: 10px;
  background: #f9fbff;
  padding: 8px 10px;
  font-size: 13px;
  color: #2a3752;
  display: flex;
  align-items: center;
  gap: 6px;
}

.delete-modal-input {
  border-left: 0;
}

.delete-modal-footer {
  padding: 10px 14px 14px;
  border-top: 1px solid #edf1f8;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 992px) {
  .streams-hero {
    margin: 0 12px 12px;
    padding: 12px;
    flex-direction: column;
    align-items: flex-start;
  }

  .streams-hero__create {
    width: 100%;
  }

  .streams-filter-card__type {
    width: 100%;
  }

  .stream-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .move-controls {
    flex-direction: row;
    order: 3;
    justify-self: end;
  }

  .stream-actions {
    order: 2;
    flex-wrap: wrap;
  }

  .stream-main-top {
    flex-wrap: wrap;
  }

  .stream-name {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .streams-filter-card .row {
    gap: .5rem;
  }

  .streams-filter-card .col,
  .streams-filter-card__type {
    width: 100% !important;
    flex: 0 0 100%;
  }

  .streams-meta {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
